<template>
    <CardWithImage
        :cta-text="getCtaLabel(selected)"
        :description="getDescription(selected)"
        :image="getImage(selected, imageLoading, imageFetchPriority)"
        :is-start-price="selected.__typename !== 'CatalogueWeddingRing'"
        :lab-grown
        :supports-express="item.supportsExpress"
        :name="getName(selected)"
        :total-sale-price="selected.salePrice.total"
        :url="routes.ring.product(selected)"
        class="product-card"
        @link-clicked="$emit('linkClicked', selected)"
    >
        <template #rating>
            <div
                v-if="item.reviewsAggregate.total"
                class="rating"
            >
                {{ item.reviewsAggregate.rating }} ({{
                    item.reviewsAggregate.total
                }})
            </div>
        </template>

        <template #metal-variation>
            <MetalVariations
                v-if="variations"
                class="metal-variation"
                :selected
                :variations
                :link="false"
                :show-title="false"
                @click="setSelected"
            />
        </template>

        <template #save-for-later>
            <SaveForLaterButton
                :product="selected"
                :page-type
            />
        </template>
    </CardWithImage>
</template>

<script lang="ts" setup generic="V extends ProductCardFragment">
import { computed, ref, type Ref, watch } from 'vue'

import { type ProductCardFragment } from '@/graphql/api'
import { isSuffixLabGrown } from '@/domain/product/isSuffixLabGrown.ts'
import { useRoutes } from '@/router/routes.ts'
import { type ImageProps } from '@/components/Image.vue'
import {
    type SaveabledProduct,
    type SaveForLaterPageType,
} from '@/components/saved-products/useSavedProducts'

import CardWithImage from '@/components/layout/cards/CardWithImage.vue'
import MetalVariations from '@/components/rings/MetalVariations.vue'
import SaveForLaterButton from '@/components/saved-products/SaveForLaterButton.vue'

const {
    item,
    imageLoading = undefined,
    imageFetchPriority = undefined,
} = defineProps<{
    item: V
    variations: V[] | undefined
    imageLoading?: ImageProps['loading']
    imageFetchPriority?: ImageProps['fetchpriority']
    pageType: SaveForLaterPageType
}>()

defineEmits<{
    linkClicked: [item: V]
}>()

const labGrown = computed(() => isSuffixLabGrown(item))

const selected: Ref<V> = ref(item) as Ref<V>

watch(
    () => item,
    () => {
        selected.value = item
    }
)

const setSelected = (item: V) => {
    selected.value = item
}

const routes = useRoutes()
</script>

<script lang="ts">
import { assertNever } from '@/helpers'
import { getGenericImageProps } from '@/components/useImageProps.ts'

const getImage = (
    item: ProductCardFragment,
    loading: ImageProps['loading'],
    fetchpriority: ImageProps['fetchpriority']
): ImageProps | ImageProps[] | undefined =>
    item.images.map((image) =>
        getGenericImageProps({
            size: image.medium,
            size_x2: image.medium_x2,
            alt: item.productName,
            loading,
            fetchpriority,
        })
    )

export const getName = (item: SaveabledProduct): string => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
        // fallthrough
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryRing':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
            return item.productName
        case 'CatalogueReadyToGoRing':
            return item.shortName
        default:
            return assertNever(item, '')
    }
}

export const getDescription = (item: SaveabledProduct): string | null => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
        // fallthrough
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryRing':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
            return item.translation.description
        case 'CatalogueReadyToGoRing':
            return item.shortDescription
        default:
            return assertNever(item, '')
    }
}

const getCtaLabel = (item: ProductCardFragment) => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
        // fallthrough
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryRing':
        // fallthrough
        case 'CatalogueReadyToGoRing':
            return 'View ring'
        case 'CatalogueJewelleryBracelet':
            return 'View bracelet'
        case 'CatalogueJewelleryEarrings':
            return 'View earrings'
        case 'CatalogueJewelleryNecklace':
            return 'View necklace'
        default:
            return assertNever(item, '')
    }
}
</script>

<style lang="scss" scoped>
.rating {
    position: relative;
    font-family: $effra-font-stack;
    font-size: 14px;
    color: $slate;

    &::before {
        position: absolute;
        content: '';
        width: 12px;
        height: 12px;
        top: 3px;
        left: -14px;
        background: url('@/assets/icons/star-filled-slate.svg');
        background-size: contain;
        background-repeat: no-repeat;
    }
}
</style>
